import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Fab,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Alert,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Checkbox,
  Paper,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { itemsApi, Item, ItemStatus } from "../../services/api";
import StatusChip from "../../components/StatusChip";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Purchased", value: "purchased" },
  { label: "Priced", value: "priced" },
  { label: "In Booth", value: "in_booth" },
  { label: "Sold", value: "sold" },
];

const MOVE_TO_OPTIONS: { label: string; value: ItemStatus }[] = [
  { label: "Purchased", value: "purchased" },
  { label: "Priced", value: "priced" },
  { label: "In Booth", value: "in_booth" },
  { label: "Sold", value: "sold" },
];

export default function ItemListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const statusFilter = searchParams.get("status") || "";
  const searchQuery = searchParams.get("search") || "";

  const fetchItems = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (statusFilter) params.status = statusFilter;
    if (searchQuery) params.search = searchQuery;

    itemsApi
      .list(params)
      .then((res) => setItems(res.data))
      .catch(() => setError("Failed to load items."))
      .finally(() => setLoading(false));
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Clear selection when filters change or items reload
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, searchQuery]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const params = new URLSearchParams(searchParams);
    if (e.target.value) {
      params.set("status", e.target.value);
    } else {
      params.delete("status");
    }
    setSearchParams(params);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const params = new URLSearchParams(searchParams);
    if (e.target.value) {
      params.set("search", e.target.value);
    } else {
      params.delete("search");
    }
    setSearchParams(params);
  };

  // Selection handlers
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleBulkStatusUpdate = async (newStatus: ItemStatus) => {
    if (selectedIds.size === 0) return;

    setBulkUpdating(true);
    try {
      const res = await itemsApi.bulkUpdateStatus(
        Array.from(selectedIds),
        newStatus
      );
      const statusLabel =
        MOVE_TO_OPTIONS.find((o) => o.value === newStatus)?.label ?? newStatus;
      setSnackbar({
        open: true,
        message: `${res.data.updated} item${res.data.updated !== 1 ? "s" : ""} moved to ${statusLabel}`,
      });
      setSelectedIds(new Set());
      fetchItems();
    } catch {
      setSnackbar({ open: true, message: "Failed to update items." });
    } finally {
      setBulkUpdating(false);
    }
  };

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setBulkDeleting(true);
    setDeleteDialogOpen(false);
    try {
      const res = await itemsApi.bulkDelete(Array.from(selectedIds));
      setSnackbar({
        open: true,
        message: `${res.data.deleted} item${res.data.deleted !== 1 ? "s" : ""} deleted`,
      });
      setSelectedIds(new Set());
      fetchItems();
    } catch {
      setSnackbar({ open: true, message: "Failed to delete items." });
    } finally {
      setBulkDeleting(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await itemsApi.exportCsv(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "items_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: "Failed to export items." });
    } finally {
      setExporting(false);
    }
  };

  const selectionActive = selectedIds.size > 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">Items</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={exporting || loading}
        >
          {exporting ? "Exporting…" : "Export"}
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, overflow: "visible" }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, flexDirection: { xs: "column", sm: "row" } }}>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusChange}
            size="small"
            sx={{ minWidth: { xs: "100%", sm: 160 } }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search items..."
            value={searchQuery}
            onChange={handleSearch}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
          />
        </Box>
      </Card>

      {/* Items Grid */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5 }}>
              <Skeleton variant="rounded" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" height={20} />
                <Skeleton width="30%" height={16} />
              </Box>
            </Box>
          ))}
        </Card>
      ) : items.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            No items found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tap the + button to add your first antique item.
          </Typography>
        </Card>
      ) : (
        <Card>
          {/* Select All header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Checkbox
              size="small"
              checked={items.length > 0 && selectedIds.size === items.length}
              indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
              onChange={toggleSelectAll}
              sx={{ mr: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary">
              {selectionActive
                ? `${selectedIds.size} of ${items.length} selected`
                : "Select all"}
            </Typography>
          </Box>

          <List disablePadding>
            {items.map((item, index) => {
              const isSelected = selectedIds.has(item.id);
              return (
                <React.Fragment key={item.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItemButton
                    onClick={() => {
                      if (selectionActive) {
                        toggleSelect(item.id);
                      } else {
                        navigate(`/items/${item.id}`);
                      }
                    }}
                    selected={isSelected}
                    sx={{ py: 1, px: 1.5, gap: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(item.id);
                      }}
                      sx={{ p: 0.5 }}
                    />
                    <ListItemAvatar sx={{ minWidth: "auto" }}>
                      {item.photo ? (
                        <Avatar
                          variant="rounded"
                          src={item.photo}
                          alt={item.description}
                          sx={{ width: 48, height: 48 }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{ width: 48, height: 48, bgcolor: "grey.100" }}
                        >
                          <ImageIcon sx={{ color: "grey.400" }} />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            component="span"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            #{item.id}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" component="span">
                          ${Number(item.purchase_price).toFixed(2)}
                          {item.sale_price && (
                            <Typography
                              component="span"
                              variant="caption"
                              color={item.status === "sold" ? "success.main" : "text.secondary"}
                              fontWeight={600}
                              sx={{ ml: 1 }}
                            >
                              {item.status === "sold" ? "Sold" : "Asking"}: ${Number(item.sale_price).toFixed(2)}
                            </Typography>
                          )}
                        </Typography>
                      }
                      sx={{ my: 0 }}
                    />
                    <StatusChip status={item.status} />
                  </ListItemButton>
                </React.Fragment>
              );
            })}
          </List>
        </Card>
      )}

      {/* FAB to add new item — hidden when selection is active */}
      {!selectionActive && (
        <Fab
          color="primary"
          aria-label="add item"
          sx={{ position: "fixed", bottom: { xs: 80, md: 24 }, right: { xs: 16, md: 24 } }}
          onClick={() => navigate("/items/new")}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Floating action bar for bulk status update */}
      {selectionActive && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            display: "flex",
            justifyContent: "center",
            pb: {
              xs: "calc(56px + env(safe-area-inset-bottom, 0px) + 12px)",
              md: "16px",
            },
            px: 2,
            pointerEvents: "none",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: { xs: "100%", sm: "auto" },
              maxWidth: 480,
              borderRadius: 3,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              pointerEvents: "auto",
            }}
          >
            <Button
              size="small"
              onClick={clearSelection}
              sx={{ minWidth: "auto", p: 0.5, color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </Button>
            <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
              {selectedIds.size} selected
            </Typography>
            <Box sx={{ flex: 1 }} />
            <TextField
              select
              label="Move to"
              size="small"
              value=""
              disabled={bulkUpdating || bulkDeleting}
              onChange={(e) =>
                handleBulkStatusUpdate(e.target.value as ItemStatus)
              }
              sx={{ minWidth: 140 }}
            >
              {MOVE_TO_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              disabled={bulkUpdating || bulkDeleting}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Paper>
        </Box>
      )}

      {/* Bulk delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Items</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete{" "}
            {selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""}? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / error snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
